import type { NovelSummary } from "../apiClient.js";
import { novelScript, scriptOfLanguage } from "./novelScript.js";

/** Novels by likes, most first; ties keep their order (the feed's own ranking). */
function byLikes(novels: readonly NovelSummary[]): NovelSummary[] {
  return [...novels].sort((a, b) => b.likes - a.likes);
}

/**
 * Picks the novel to offer the author while their own is generating.
 *
 * The rule (from the product spec): suggest the most-liked published novel; if
 * they have already finished it, fall to the next most-liked, and so on, until
 * one they have not finished. If they have finished every candidate, suggest a
 * replay of the most-liked one rather than nothing.
 *
 * `novels` is the public catalog feed (already loaded by the App). "Finished"
 * is a per-browser signal (see `isNovelFinished`). Pure and deterministic:
 * ties keep the input order, which is the feed's own quality ranking.
 */
export function pickSuggestedNovel(
  novels: readonly NovelSummary[],
  isFinished: (id: string) => boolean
): NovelSummary | null {
  if (novels.length === 0) {
    return null;
  }
  // Stable sort by likes desc; equal-like novels keep their feed order.
  const ranked = byLikes(novels);
  const fresh = ranked.find((novel) => !isFinished(novel.id));
  // Everything played → replay the most-liked (ranked[0]).
  return fresh ?? ranked[0];
}

/**
 * The novel the ending panel offers next ("Read another story"): never the one
 * just finished and never one this browser has finished — the most-liked
 * unfinished novel written in the same script as the one just read, else the
 * most-liked unfinished novel in any script. A catalog summary carries no
 * language, so the script of its title and description stands in for one
 * (lib/novelScript.ts): someone who just read an English story is not sent to a
 * Russian one while an English one is left. Null when every other novel is
 * finished (or there is none) — the panel then hides the button rather than
 * offer a replay.
 */
export function pickNextNovel(
  novels: readonly NovelSummary[],
  current: { id: string; language: string },
  isFinished: (id: string) => boolean
): NovelSummary | null {
  const unfinished = byLikes(novels.filter((novel) => novel.id !== current.id && !isFinished(novel.id)));
  const script = scriptOfLanguage(current.language);
  return unfinished.find((novel) => novelScript(novel) === script) ?? unfinished[0] ?? null;
}
